import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState, } from 'react';
import { Provider, useStore } from 'jotai/react';
import { createPatchedStore, isTopLevelScope } from './patchedStore.js';
import { createScope } from './scope.js';
const ScopeContext = createContext({ scope: undefined, baseStore: undefined });
export function ScopeProvider({ atoms, atomFamilies, children, debugName, }) {
    const parentStore = useStore();
    let { scope: parentScope, baseStore = parentStore } = useContext(ScopeContext);
    // if this ScopeProvider is the first descendant scope under Provider then it is the top level scope
    // https://github.com/jotaijs/jotai-scope/pull/33#discussion_r1604268003
    if (isTopLevelScope(parentStore)) {
        parentScope = undefined;
        baseStore = parentStore;
    }
    // atomSet is used to detect if the atoms prop has changed.
    const atomSet = new Set(atoms);
    const atomFamilySet = new Set(atomFamilies);
    function initialize() {
        const scope = createScope(atomSet, atomFamilySet, parentScope, debugName);
        return {
            patchedStore: createPatchedStore(baseStore, scope),
            scopeContext: { scope, baseStore },
            hasChanged(current) {
                return (parentScope !== current.parentScope ||
                    baseStore !== current.baseStore ||
                    !isEqualSet(atomSet, current.atomSet) ||
                    !isEqualSet(atomFamilySet, current.atomFamilySet));
            },
        };
    }
    const [state, setState] = useState(initialize);
    const { hasChanged, scopeContext, patchedStore } = state;
    if (hasChanged({ parentScope, atomSet, atomFamilySet, baseStore })) {
        scopeContext.scope?.cleanup();
        setState(initialize);
    }
    const { cleanup } = scopeContext.scope;
    useEvent(() => cleanup, []);
    return (_jsx(ScopeContext.Provider, { value: scopeContext, children: _jsx(Provider, { store: patchedStore, children: children }) }));
}
function isEqualSet(a, b) {
    return a === b || (a.size === b.size && Array.from(a).every((v) => b.has(v)));
}
function useEvent(fn, deps) {
    const ref = useRef(fn);
    ref.current = fn;
    useEffect(() => ref.current(), deps);
}
//# sourceMappingURL=ScopeProvider.js.map